import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import type { Product } from '@/types'
import { ProductCard } from '@/features/ecommerce/components/ProductCard'

interface Category {
  id: number
  name: string
  slug: string
}

interface ProductsLoaderData {
  products: Product[]
  categories: Category[]
}

export const Route = createFileRoute('/products')({
  component: ProductsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    category: (search.category as string) || '',
    sort: (search.sort as string) || 'featured',
    minPrice: search.minPrice ? Number(search.minPrice) : undefined,
    maxPrice: search.maxPrice ? Number(search.maxPrice) : undefined,
    inStock: search.inStock === 'true' ? true : undefined,
    material: (search.material as string) || '',
    style: (search.style as string) || '',
  }),
  loader: async (): Promise<ProductsLoaderData> => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        fetch('/data/products.json'),
        fetch('/data/categories.json'),
      ])

      const products: Product[] = await productsRes.json()
      let categories: Category[] = []

      try {
        categories = await categoriesRes.json()
      } catch {
        // Categories might not exist yet
      }

      return { products, categories }
    } catch {
      return { products: [], categories: [] }
    }
  },
})

type SortOption = 'featured' | 'price_asc' | 'price_desc' | 'newest' | 'name_asc'

function ProductsPage() {
  const { products, categories } = Route.useLoaderData()
  const search = useSearch({ from: '/products' })
  const _navigate = Route.useNavigate()
  void _navigate // Reserved for future use

  // Local filter state
  const [sortBy, setSortBy] = useState<SortOption>((search.sort as SortOption) || 'featured')
  const [selectedCategory, setSelectedCategory] = useState(search.category || '')
  const [priceRange, setPriceRange] = useState<{ min: string; max: string }>({
    min: search.minPrice?.toString() || '',
    max: search.maxPrice?.toString() || '',
  })
  const [inStockOnly, setInStockOnly] = useState(search.inStock || false)
  const [selectedMaterial, setSelectedMaterial] = useState(search.material || '')
  const [selectedStyle, setSelectedStyle] = useState(search.style || '')
  const [showFilters, setShowFilters] = useState(false)

  // Extract unique materials and styles from products
  const materials = useMemo(() => {
    const uniqueMaterials = new Set<string>()
    products.forEach((p) => {
      if ((p as any).material) uniqueMaterials.add((p as any).material)
    })
    return Array.from(uniqueMaterials).sort()
  }, [products])

  const styles = useMemo(() => {
    const uniqueStyles = new Set<string>()
    products.forEach((p) => {
      if ((p as any).style) uniqueStyles.add((p as any).style)
    })
    return Array.from(uniqueStyles).sort()
  }, [products])

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...products]

    // Category filter
    if (selectedCategory) {
      result = result.filter(
        (p) => p.category?.slug === selectedCategory || p.category?.name === selectedCategory
      )
    }

    // Price filter
    if (priceRange.min) {
      const minPrice = parseFloat(priceRange.min)
      if (!isNaN(minPrice)) {
        result = result.filter((p) => p.price >= minPrice)
      }
    }
    if (priceRange.max) {
      const maxPrice = parseFloat(priceRange.max)
      if (!isNaN(maxPrice)) {
        result = result.filter((p) => p.price <= maxPrice)
      }
    }

    // In stock filter
    if (inStockOnly) {
      result = result.filter((p) => p.in_stock)
    }

    // Material filter
    if (selectedMaterial) {
      result = result.filter((p) => (p as any).material === selectedMaterial)
    }

    // Style filter
    if (selectedStyle) {
      result = result.filter((p) => (p as any).style === selectedStyle)
    }

    // Sorting
    switch (sortBy) {
      case 'price_asc':
        result.sort((a, b) => a.price - b.price)
        break
      case 'price_desc':
        result.sort((a, b) => b.price - a.price)
        break
      case 'name_asc':
        result.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'newest':
        // Assuming products are already sorted by newest first from API
        break
      case 'featured':
      default:
        // Featured products first
        result.sort((a, b) => {
          if ((a as any).is_featured && !(b as any).is_featured) return -1
          if (!(a as any).is_featured && (b as any).is_featured) return 1
          return 0
        })
        break
    }

    return result
  }, [products, selectedCategory, priceRange, inStockOnly, selectedMaterial, selectedStyle, sortBy])

  const clearFilters = () => {
    setSelectedCategory('')
    setPriceRange({ min: '', max: '' })
    setInStockOnly(false)
    setSelectedMaterial('')
    setSelectedStyle('')
    setSortBy('featured')
  }

  const hasActiveFilters =
    selectedCategory || priceRange.min || priceRange.max || inStockOnly || selectedMaterial || selectedStyle

  const currencySymbol = '₹'

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">Products</h1>
          <p className="text-gray-600 mt-1">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            {hasActiveFilters && ' (filtered)'}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Mobile filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 px-4 py-2 border rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filters
          </button>

          {/* Sort dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="featured">Sort by: Featured</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="name_asc">Name: A to Z</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      <div className="flex gap-8">
        {/* Sidebar Filters */}
        <aside
          className={`${
            showFilters ? 'block' : 'hidden'
          } md:block w-full md:w-64 flex-shrink-0 absolute md:relative bg-white md:bg-transparent z-10 p-4 md:p-0 border md:border-0 rounded-lg md:rounded-none`}
        >
          <div className="space-y-6">
            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear all filters
              </button>
            )}

            {/* Categories */}
            {categories.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Category</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="category"
                      checked={!selectedCategory}
                      onChange={() => setSelectedCategory('')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">All Categories</span>
                  </label>
                  {categories.map((cat) => (
                    <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        checked={selectedCategory === cat.slug}
                        onChange={() => setSelectedCategory(cat.slug)}
                        className="text-blue-600"
                      />
                      <span className="text-sm">{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Price Range */}
            <div>
              <h3 className="font-semibold mb-3">Price Range</h3>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange((prev) => ({ ...prev, min: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange((prev) => ({ ...prev, max: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">In {currencySymbol}</p>
            </div>

            {/* In Stock */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                  className="text-blue-600 rounded"
                />
                <span className="text-sm font-medium">In Stock Only</span>
              </label>
            </div>

            {/* Materials */}
            {materials.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Material</h3>
                <select
                  value={selectedMaterial}
                  onChange={(e) => setSelectedMaterial(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">All Materials</option>
                  {materials.map((mat) => (
                    <option key={mat} value={mat}>
                      {mat}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Styles */}
            {styles.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Style</h3>
                <select
                  value={selectedStyle}
                  onChange={(e) => setSelectedStyle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">All Styles</option>
                  {styles.map((style) => (
                    <option key={style} value={style}>
                      {style}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Mobile close button */}
          <button
            onClick={() => setShowFilters(false)}
            className="md:hidden mt-6 w-full py-2 bg-blue-600 text-white rounded-lg font-medium"
          >
            Apply Filters
          </button>
        </aside>

        {/* Product Grid */}
        <div className="flex-1">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg
                className="w-16 h-16 text-gray-300 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-gray-600 mb-4">No products match your filters.</p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-blue-600 hover:underline">
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
